import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Image, Menu, X, Globe, Search } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

export function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [language, setLanguage] = React.useState<'ko' | 'en'>('ko');

  const isPublicPage = ['/login', '/signup', '/find-account', '/reset-password'].includes(location.pathname);
  const isLandingPage = location.pathname === '/';

  const scrollToSection = (sectionId: string) => {
    if (!isLandingPage) {
      navigate('/', { state: { scrollTo: sectionId } });
      return;
    }
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // 로그인된 사용자는 공개 페이지에서 헤더를 보지 않음
  if (user && isPublicPage) {
    return null;
  }

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* 로고 */}
          <Link to={user ? '/dashboard' : '/'} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <img src="/favicon-32x32.png" alt="ALT Generation Logo" className="h-8 w-8" />
            <span className="text-xl font-bold text-gray-900">ALT Generation</span>
          </Link>

          {/* 데스크탑 네비게이션 */}
          <nav className="hidden md:flex items-center gap-6">
            {!user ? (
              <>
                <button 
                  onClick={() => scrollToSection('features')} 
                  className="text-gray-700 hover:text-primary font-medium transition-colors"
                >
                  특징
                </button>
                <button
                  onClick={() => navigate('/notices')}
                  className="text-gray-700 hover:text-primary font-medium transition-colors"
                >
                  공지사항
                </button>
                <button 
                  onClick={() => scrollToSection('pricing')} 
                  className="text-gray-700 hover:text-primary font-medium transition-colors"
                >
                  요금제
                </button>
                <button 
                  onClick={() => scrollToSection('about')} 
                  className="text-gray-700 hover:text-primary font-medium transition-colors"
                >
                  회사 소개
                </button>
                <div className="flex items-center gap-3 ml-4">
                  <button
                    onClick={() => setLanguage(language === 'ko' ? 'en' : 'ko')}
                    className="hidden flex items-center gap-1 px-2 py-1 text-sm text-gray-600 hover:text-primary hover:bg-gray-100 rounded-md transition-colors"
                    title="언어 변경"
                  >
                    <Globe className="h-4 w-4" />
                    <span>{language === 'ko' ? '한국어' : 'English'}</span>
                  </button>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/diagnose')}
                    className="font-medium border-green-500 text-green-700 hover:bg-green-50 gap-1.5"
                  >
                    <Search className="h-4 w-4" />
                    무료 진단
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => navigate('/login')}
                    className="font-medium"
                  >
                    로그인
                  </Button>
                  <Button 
                    onClick={() => navigate('/signup')}
                    className="font-medium"
                  >
                    무료 시작하기
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Link 
                  to="/dashboard" 
                  className={`text-gray-700 hover:text-primary font-medium transition-colors ${
                    location.pathname === '/dashboard' ? 'text-primary' : ''
                  }`}
                >
                  대시보드
                </Link>
                <Link 
                  to="/dashboard/crawler/website" 
                  className={`text-gray-700 hover:text-primary font-medium transition-colors ${
                    location.pathname.includes('/crawler') ? 'text-primary' : ''
                  }`}
                >
                  ALT 생성
                </Link>
                <Link 
                  to="/dashboard/profile/edit" 
                  className={`text-gray-700 hover:text-primary font-medium transition-colors ${
                    location.pathname.includes('/profile') ? 'text-primary' : ''
                  }`}
                >
                  프로필
                </Link>
                <div className="flex items-center gap-3 ml-4">
                  <button
                    onClick={() => setLanguage(language === 'ko' ? 'en' : 'ko')}
                    className="hidden flex items-center gap-1 px-2 py-1 text-sm text-gray-600 hover:text-primary hover:bg-gray-100 rounded-md transition-colors"
                    title="언어 변경"
                  >
                    <Globe className="h-4 w-4" />
                    <span>{language === 'ko' ? '한국어' : 'English'}</span>
                  </button>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/diagnose')}
                    className="font-medium border-green-500 text-green-700 hover:bg-green-50 gap-1.5"
                  >
                    <Search className="h-4 w-4" />
                    무료 진단
                  </Button>
                  <span className="text-sm text-gray-600">{user.name}님</span>
                  <Button 
                    variant="outline" 
                    onClick={handleLogout}
                    className="font-medium"
                  >
                    로그아웃
                  </Button>
                </div>
              </>
            )}
          </nav>

          {/* 모바일 메뉴 버튼 */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-gray-700 hover:text-primary transition-colors"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* 모바일 메뉴 */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t">
            <nav className="flex flex-col gap-4">
              {!user ? (
                <>
                  <button 
                    onClick={() => {
                      scrollToSection('features');
                      setMobileMenuOpen(false);
                    }}
                    className="text-gray-700 hover:text-primary font-medium transition-colors py-2 text-left"
                  >
                    특징
                  </button>
                  <button
                    onClick={() => {
                      scrollToSection('pricing');
                      setMobileMenuOpen(false);
                    }}
                    className="text-gray-700 hover:text-primary font-medium transition-colors py-2 text-left"
                  >
                    요금제
                  </button>
                  <Link
                    to="/notices"
                    className="text-gray-700 hover:text-primary font-medium transition-colors py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    공지사항
                  </Link>
                  <button
                    onClick={() => {
                      scrollToSection('about');
                      setMobileMenuOpen(false);
                    }}
                    className="text-gray-700 hover:text-primary font-medium transition-colors py-2 text-left"
                  >
                    회사 소개
                  </button>
                  <div className="flex flex-col gap-2 pt-2 border-t">
                    <button
                      onClick={() => setLanguage(language === 'ko' ? 'en' : 'ko')}
                      className="flex items-center gap-2 py-2 text-sm text-gray-600 hover:text-primary transition-colors"
                    >
                      <Globe className="h-4 w-4" />
                      {language === 'ko' ? '한국어' : 'English'}
                    </button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        navigate('/diagnose');
                        setMobileMenuOpen(false);
                      }}
                      className="w-full font-medium border-green-500 text-green-700 hover:bg-green-50 gap-2"
                    >
                      <Search className="h-4 w-4" />
                      무료 진단
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        navigate('/login');
                        setMobileMenuOpen(false);
                      }}
                      className="w-full font-medium"
                    >
                      로그인
                    </Button>
                    <Button 
                      onClick={() => {
                        navigate('/signup');
                        setMobileMenuOpen(false);
                      }}
                      className="w-full font-medium"
                    >
                      무료 시작하기
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <Link 
                    to="/dashboard" 
                    className="text-gray-700 hover:text-primary font-medium transition-colors py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    대시보드
                  </Link>
                  <Link 
                    to="/dashboard/crawler/website" 
                    className="text-gray-700 hover:text-primary font-medium transition-colors py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    ALT 생성
                  </Link>
                  <Link 
                    to="/dashboard/profile/edit" 
                    className="text-gray-700 hover:text-primary font-medium transition-colors py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    프로필
                  </Link>
                  <div className="flex flex-col gap-2 pt-2 border-t">
                    <button
                      onClick={() => setLanguage(language === 'ko' ? 'en' : 'ko')}
                      className="flex items-center gap-2 py-2 text-sm text-gray-600 hover:text-primary transition-colors"
                    >
                      <Globe className="h-4 w-4" />
                      {language === 'ko' ? '한국어' : 'English'}
                    </button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        navigate('/diagnose');
                        setMobileMenuOpen(false);
                      }}
                      className="w-full font-medium border-green-500 text-green-700 hover:bg-green-50 gap-2"
                    >
                      <Search className="h-4 w-4" />
                      무료 진단
                    </Button>
                    <span className="text-sm text-gray-600 py-2">{user.name}님</span>
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        handleLogout();
                        setMobileMenuOpen(false);
                      }}
                      className="w-full font-medium"
                    >
                      로그아웃
                    </Button>
                  </div>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
